import { createSlice, current } from '@reduxjs/toolkit'

import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { DefaultMap } from '../../utils'
import {
  loadFavoriteIds,
  saveFavoriteIds,
  unionFavoriteIds
} from './favoritesStorage'

// Read initial favorites from localStorage at module load time.
// The convention year is not yet known here (config loads asynchronously),
// so we use the current calendar year as the bootstrap key. Once
// syncFavoritesOnLoad resolves the real year, the correct keyed value takes over.
const bootstrapYear = new Date().getFullYear()
const bootstrapIds = loadFavoriteIds(bootstrapYear)

const initialState = {
  data: [],
  error: '',
  updated: '',
  checksum: '',
  favorite: [],
  assigned: [],
  favorite_ids: bootstrapIds,
  accordition: {}
}

function sortByStartEnd (event1, event2) {
  const eventDate1 = {
    start: new Date(event1.start),
    end: new Date(event1.end)
  }

  const eventDate2 = {
    start: new Date(event2.start),
    end: new Date(event2.end)
  }

  const duration1 = eventDate1.end - eventDate1.start
  const duration2 = eventDate2.end - eventDate2.start
  const startEquals = eventDate1.start <= eventDate2.start && eventDate1.start >= eventDate2.start

  if (eventDate1.start < eventDate2.start) {
    return -1
  } else if (startEquals && duration1 <= duration2) {
    return -1
  }

  return 1
}

function pushToEventArray (arr, event, filter, favorites) {
  let customizedEvent = { ...event }

  if (favorites.includes(customizedEvent.id)) {
    customizedEvent = { ...customizedEvent, favorite: true }
  } else {
    customizedEvent = { ...customizedEvent, favorite: undefined }
  }

  if (!filter(customizedEvent)) {
    return
  }

  arr.push(customizedEvent)
}

function processEvents (events, state, favorites, accordition) {
  events.sort(sortByStartEnd)

  const allEvents = new DefaultMap(() => [])
  const favoriteEvents = new DefaultMap(() => [])
  const assignedEvents = new DefaultMap(() => [])

  const today = new Date()
  let earliest = events.length > 0 ? new Date(events[0].start) : today
  let latest = events.length > 0 ? new Date(events[0].end) : today
  let assignedCount = 0

  events.forEach((event) => {
    const startDate = new Date(event.start)
    const endDate = new Date(event.end)
    const key = startDate.toISOString().substring(0, 10)

    earliest = startDate <= earliest ? startDate : earliest
    latest = endDate >= latest ? endDate : earliest

    const allEventsValues = allEvents.get(key)
    pushToEventArray(allEventsValues, event, () => true, favorites)

    const favoriteEventsValues = favoriteEvents.get(key)
    pushToEventArray(favoriteEventsValues, event, (e) => e?.favorite, favorites)

    const assignedEventValues = assignedEvents.get(key)
    pushToEventArray(assignedEventValues, event, (e) => e?.assigned, favorites)
    assignedCount = (event?.assigned) ? assignedCount + 1 : assignedCount
  })
  const expanded = today >= earliest && today <= latest ? today : earliest
  return {
    ...state,
    data: [...allEvents.entries()],
    favorite: [...favoriteEvents.entries()],
    assigned: [...assignedEvents.entries()],
    assignedCount,
    favorite_ids: [...favorites],
    accordition: {
      ...accordition,
      expanded: accordition?.reset ? expanded.toISOString().substring(0, 10) : accordition.expanded,
      reset: false
    }
  }
}

const events = createSlice({
  name: 'events',
  initialState,
  reducers: {
    fetchEventsSuccess (state, action) {
      const { data, checksum, generated } = action.payload
      const { favorite_ids, accordition } = current(state)

      return processEvents(data, {
        ...state,
        checksum,
        updated: generated,
        error: ''
      }, favorite_ids, accordition)
    },
    fetchEventsError (state, action) {
      const { detail } = action.payload

      return {
        ...state,
        error: detail
      }
    },
    // Toggles the favorite status of a single event.
    // Writes updated IDs to localStorage synchronously.
    // Server sync (PUT) is handled by the toggleFavoriteWithSync thunk.
    setFavoriteForEvent (state, action) {
      const { id, favorite, year } = action.payload
      const { data, favorite_ids, accordition } = current(state)
      const eventsFlat = data.flatMap((entry) => entry[1])
      const ids = new Set(favorite_ids)
      if (favorite) {
        ids.add(id)
      } else {
        ids.delete(id)
      }

      const updatedIds = [...ids]
      saveFavoriteIds(year ?? bootstrapYear, updatedIds)

      return processEvents(eventsFlat, {
        ...state
      }, updatedIds, accordition)
    },
    // Called after a successful server fetch: unions local + server IDs,
    // persists to localStorage, and reprocesses event lists.
    serverFavoritesLoaded (state, action) {
      const { ids: serverIds, year } = action.payload
      const { data, favorite_ids, accordition } = current(state)
      const eventsFlat = data.flatMap((entry) => entry[1])

      const mergedIds = unionFavoriteIds(favorite_ids, serverIds)
      saveFavoriteIds(year ?? bootstrapYear, mergedIds)

      return processEvents(eventsFlat, {
        ...state
      }, mergedIds, accordition)
    },
    // Replaces the favorite_ids list entirely (used by restore-on-wipe flow).
    setFavoriteIds (state, action) {
      const { ids, year } = action.payload
      const { data, accordition } = current(state)
      const eventsFlat = data.flatMap((entry) => entry[1])

      saveFavoriteIds(year ?? bootstrapYear, ids)

      return processEvents(eventsFlat, {
        ...state
      }, ids, accordition)
    },
    setAccorditionExpanded (state, action) {
      const { expanded } = action.payload
      const { accordition } = current(state)
      return {
        ...state,
        accordition: {
          ...accordition,
          expanded
        }
      }
    },
    setAccorditionReset (state, _action) {
      const { accordition } = current(state)
      return {
        ...state,
        accordition: {
          ...accordition,
          reset: true
        }
      }
    }
  }
})

// favorite_ids is blacklisted: it lives in companion_favorite_events_YEAR
// (raw localStorage) and survives Redux Persist resets triggered by app
// version bumps. On each load the slice bootstraps from that raw key.
const persistConfig = {
  key: 'events',
  storage,
  blacklist: ['favorite_ids']
}

export const {
  fetchEventsSuccess,
  fetchEventsError,
  setFavoriteForEvent,
  serverFavoritesLoaded,
  setFavoriteIds,
  setAccorditionExpanded,
  setAccorditionReset
} = events.actions

export const EventsReducer = persistReducer(persistConfig, events.reducer)
